import useProductStore from '@/store/product.store';
import { imageAndFabricWorldUpdater } from '@/composables/useMaterialUpdater';
import useConfigStore from '@/store/config.store';
import { BAT_SCALE_FACTOR, IMG_HEIGHT, IMG_WIDTH, MM_TO_PIXEL } from '@/guidetti/projectConstantes';
import { createUniqueId } from '@/utils/createUniqueId';
import { generateThumbnail } from '@/utils/thumbnail';
import { jsPDF as JsPDF } from 'jspdf';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import type { ICart } from '@/typescript/interfaces/ICart';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextField } from '@/typescript/interfaces/ITextField';

async function addToCart(preDefinedUniqueId?: string): Promise<ICart> {
    const productStore = useProductStore();
    const { fonts, uniqueId } = storeToRefs(useConfigStore());
    const { selectedProductConfiguration, selectedProduct } = storeToRefs(productStore);
    const poleColor = computed(() => selectedProductConfiguration.value.poleColor);
    const filteredFields = computed(() =>
        selectedProductConfiguration.value.fields.filter(
            (filteredField) =>
                filteredField.transform.position.x + filteredField.transform.position.y !== 0 ||
                (filteredField as IImageField)?.url !== undefined ||
                (filteredField as ITextField)?.text !== undefined,
        ),
    );

    let error = false;
    let screenshot: Blob | Error = new Blob();
    let templateId = 0;
    let pdf = new Blob();
    let errorMessage = '';
    // obsidian-api passes a predefined return callback if no argument is given
    if (typeof preDefinedUniqueId === 'string') {
        uniqueId.value = preDefinedUniqueId;
    } else {
        uniqueId.value = createUniqueId();
    }
    const updateCustomMaterial = imageAndFabricWorldUpdater(poleColor, fonts, BAT_SCALE_FACTOR, false, uniqueId.value);
    try {
        screenshot = await generateThumbnail({ selectedProduct, fonts, poleColor });
        templateId = productStore.selectedProduct.id || 0;
        const update = await updateCustomMaterial(filteredFields.value);
        pdf = await generatePdf(update?.canvas.toDataURL('image/png', 1));
    } catch (e) {
        error = true;
        errorMessage = `${e}`;
    }
    return {
        error,
        error_message: errorMessage,
        screenshot,
        template_id: templateId,
        uniqueId: uniqueId.value,
        pdf,
    };
}

async function generatePdf(imgBAT?: string): Promise<Blob> {
    // Convert pixels to millimeters
    const doc = new JsPDF({
        unit: 'mm',
        format: [IMG_WIDTH, IMG_HEIGHT],
        userUnit: BAT_SCALE_FACTOR,
        compress: false,
    });
    doc.setProperties({
        title: 'BAT',
        subject: 'Bon à tirer',
    });

    if (imgBAT) {
        doc.addImage(imgBAT, 'jpeg', -1 / MM_TO_PIXEL, 0, IMG_WIDTH + 1 / MM_TO_PIXEL, IMG_HEIGHT, undefined);
    }
    return doc.output('blob');
}

export { addToCart };
